import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Plus, Mail, Phone, Video, FileText, ArrowUpRight, ArrowDownLeft, X,
  Sparkles, Trash2, CalendarClock, Users, Megaphone, Target,
} from "lucide-react";

const CHANNELS = [
  { key: "email", label: "Email", icon: Mail },
  { key: "call", label: "Call", icon: Phone },
  { key: "meeting", label: "Meeting", icon: Video },
  { key: "note", label: "Note", icon: FileText },
];

const CHANNEL_COLORS = {
  email: "text-blue-700 bg-blue-100 border-blue-200",
  call: "text-emerald-700 bg-emerald-100 border-emerald-200",
  meeting: "text-violet-700 bg-violet-100 border-violet-200",
  note: "text-amber-700 bg-amber-100 border-amber-200",
};

const FREQUENCIES = ["Daily", "Weekly", "Bi-weekly", "Monthly", "Milestone", "Ad-hoc"];
const PLAN_CHANNELS = ["Email", "Meeting", "Report", "Call", "Dashboard"];

const FREQ_BADGE = {
  Daily: "bg-rose-100 text-rose-700",
  Weekly: "bg-violet-100 text-violet-700",
  "Bi-weekly": "bg-indigo-100 text-indigo-700",
  Monthly: "bg-blue-100 text-blue-700",
  Milestone: "bg-amber-100 text-amber-700",
  "Ad-hoc": "bg-gray-100 text-gray-600",
};

const BLANK_PLAN = { audience: "", purpose: "", frequency: "Weekly", channel: "Email", owner: "", format: "", notes: "" };

// ── Stakeholder analysis ──
const ENGAGEMENTS = ["unaware", "resistant", "neutral", "supportive", "leading"];
const ENGAGEMENT_BADGE = {
  unaware: "bg-gray-100 text-gray-600",
  resistant: "bg-rose-100 text-rose-700",
  neutral: "bg-amber-100 text-amber-700",
  supportive: "bg-emerald-100 text-emerald-700",
  leading: "bg-violet-100 text-violet-700",
};
const QUADRANTS = {
  manage_closely: { label: "Manage Closely", sub: "High influence · High interest", color: "bg-violet-50 border-violet-200", dot: "bg-violet-500" },
  keep_satisfied: { label: "Keep Satisfied", sub: "High influence · Low interest", color: "bg-blue-50 border-blue-200", dot: "bg-blue-500" },
  keep_informed: { label: "Keep Informed", sub: "Low influence · High interest", color: "bg-emerald-50 border-emerald-200", dot: "bg-emerald-500" },
  monitor: { label: "Monitor", sub: "Low influence · Low interest", color: "bg-gray-50 border-gray-200", dot: "bg-gray-400" },
};
const quadrantOf = (influence, interest) =>
  influence === "high" && interest === "high" ? "manage_closely"
  : influence === "high" ? "keep_satisfied"
  : interest === "high" ? "keep_informed" : "monitor";
const BLANK_SH = { name: "", role: "", influence: "high", interest: "high", engagement: "neutral", strategy: "", notes: "" };

export default function ClientComms() {
  const [tab, setTab] = useState("plan");
  const [comms, setComms] = useState([]);
  const [plan, setPlan] = useState([]);
  const [projects, setProjects] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [filter, setFilter] = useState({ project_id: "", channel: "" });
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ project_id: "", contact_id: "", direction: "outbound", channel: "email", subject: "", body: "", sent_at: new Date().toISOString().slice(0, 16) });
  const [saving, setSaving] = useState(false);
  const [planForm, setPlanForm] = useState(null); // null | {entry} for add/edit modal
  const [planSaving, setPlanSaving] = useState(false);
  const [stakeholders, setStakeholders] = useState([]);
  const [shForm, setShForm] = useState(null);
  const [shSaving, setShSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filter.project_id) params.set("project_id", filter.project_id);
      if (filter.channel) params.set("channel", filter.channel);
      const planParams = new URLSearchParams();
      if (filter.project_id) planParams.set("project_id", filter.project_id);
      const [cRes, pRes, ctRes, planRes, shRes] = await Promise.all([
        api.get(`/client-comms?${params}`),
        api.get("/projects").catch(() => ({ data: [] })),
        api.get("/contacts").catch(() => ({ data: [] })),
        api.get(`/client-comms/plan?${planParams}`).catch(() => ({ data: [] })),
        api.get(`/client-comms/stakeholders?${planParams}`).catch(() => ({ data: [] })),
      ]);
      setComms(cRes.data || []);
      setProjects(pRes.data || []);
      setContacts(ctRes.data || []);
      setPlan(planRes.data || []);
      setStakeholders(shRes.data || []);
    } catch {
      toast.error("Failed to load communications");
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  // ── Log handlers ──
  const save = async () => {
    if (!form.subject || !form.body) { toast.error("Subject and body required"); return; }
    setSaving(true);
    try {
      await api.post("/client-comms", { ...form, sent_at: form.sent_at ? new Date(form.sent_at).toISOString() : undefined });
      toast.success("Communication logged");
      setShowModal(false);
      setForm({ project_id: "", contact_id: "", direction: "outbound", channel: "email", subject: "", body: "", sent_at: new Date().toISOString().slice(0, 16) });
      load();
    } catch { toast.error("Failed to save"); } finally { setSaving(false); }
  };

  const remove = async (c) => {
    try { await api.delete(`/client-comms/${c.comm_id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed to delete"); }
  };

  // ── Plan handlers ──
  const savePlan = async () => {
    if (!planForm.audience.trim() || !planForm.purpose.trim()) { toast.error("Audience and purpose are required"); return; }
    setPlanSaving(true);
    try {
      if (planForm.entry_id) {
        await api.put(`/client-comms/plan/${planForm.entry_id}`, planForm);
      } else {
        await api.post("/client-comms/plan", { ...planForm, project_id: filter.project_id || null });
      }
      toast.success("Communication plan saved");
      setPlanForm(null);
      load();
    } catch { toast.error("Failed to save plan entry"); } finally { setPlanSaving(false); }
  };

  const removePlan = async (entry) => {
    try { await api.delete(`/client-comms/plan/${entry.entry_id}`); toast.success("Removed"); load(); }
    catch { toast.error("Failed to remove"); }
  };

  const seedTemplate = async () => {
    try {
      const qs = filter.project_id ? `?project_id=${filter.project_id}` : "";
      await api.post(`/client-comms/plan/seed-template${qs}`);
      toast.success("Starter communication plan added");
      load();
    } catch { toast.error("Failed to load template"); }
  };

  // ── Stakeholder handlers ──
  const saveSh = async () => {
    if (!shForm.name.trim()) { toast.error("Name is required"); return; }
    setShSaving(true);
    try {
      if (shForm.stakeholder_id) {
        await api.put(`/client-comms/stakeholders/${shForm.stakeholder_id}`, shForm);
      } else {
        await api.post("/client-comms/stakeholders", { ...shForm, project_id: filter.project_id || null });
      }
      toast.success("Stakeholder saved");
      setShForm(null);
      load();
    } catch { toast.error("Failed to save stakeholder"); } finally { setShSaving(false); }
  };

  const removeSh = async (s) => {
    try { await api.delete(`/client-comms/stakeholders/${s.stakeholder_id}`); toast.success("Removed"); load(); }
    catch { toast.error("Failed to remove"); }
  };

  const projectName = (id) => projects.find(p => p.project_id === id)?.name || "—";
  const contactName = (id) => { const c = contacts.find(c => c.contact_id === id || c.id === id); return c ? (c.name || c.email) : id; };
  const ChIcon = ({ channel }) => { const C = CHANNELS.find(c => c.key === channel); return C ? <C.icon className="w-4 h-4" /> : null; };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-widest text-violet-600 mb-0.5">Stakeholder Engagement</p>
          <h1 className="text-2xl font-bold text-gray-900">Project Communications</h1>
          <p className="text-sm text-gray-500 mt-1">Plan how, when and to whom you communicate — and log what was sent.</p>
        </div>
        {tab === "plan" && (
          <div className="flex gap-2">
            <Button onClick={seedTemplate} variant="outline" className="rounded-full border-gray-200 text-gray-700 hover:border-violet-300 hover:text-violet-700">
              <Sparkles className="w-4 h-4 mr-1.5" /> Use template
            </Button>
            <Button onClick={() => setPlanForm({ ...BLANK_PLAN })} className="rounded-full bg-violet-600 hover:bg-violet-700 text-white font-semibold shadow-md shadow-violet-200">
              <Plus className="w-4 h-4 mr-1.5" /> Add plan row
            </Button>
          </div>
        )}
        {tab === "stakeholders" && (
          <Button onClick={() => setShForm({ ...BLANK_SH })} className="rounded-full bg-violet-600 hover:bg-violet-700 text-white font-semibold shadow-md shadow-violet-200">
            <Plus className="w-4 h-4 mr-1.5" /> Add stakeholder
          </Button>
        )}
        {tab === "log" && (
          <Button onClick={() => setShowModal(true)} className="rounded-full bg-violet-600 hover:bg-violet-700 text-white font-semibold shadow-md shadow-violet-200">
            <Plus className="w-4 h-4 mr-1.5" /> Log communication
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200 mb-6 mt-4">
        {[
          { key: "plan", label: "Communication Plan", icon: CalendarClock },
          { key: "stakeholders", label: "Stakeholder Analysis", icon: Target },
          { key: "log", label: "Communication Log", icon: Mail },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === key ? "border-violet-600 text-violet-700" : "border-transparent text-gray-500 hover:text-gray-800"
            }`}
            data-testid={`comms-tab-${key}`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
        <div className="ml-auto">
          <select
            value={filter.project_id}
            onChange={e => setFilter(f => ({ ...f, project_id: e.target.value }))}
            className="rounded-xl border border-gray-200 bg-gray-50 text-gray-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
          >
            <option value="">All projects</option>
            {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
          </select>
        </div>
      </div>

      {/* ── PLAN TAB ── */}
      {tab === "plan" && (
        plan.length === 0 ? (
          <div className="text-center py-20 border-2 border-dashed border-gray-200 rounded-2xl">
            <div className="w-14 h-14 rounded-2xl bg-violet-100 grid place-items-center mx-auto mb-4">
              <Megaphone className="w-7 h-7 text-violet-500" />
            </div>
            <h3 className="font-semibold text-gray-700">No communication plan yet</h3>
            <p className="text-sm text-gray-400 mt-1 mb-5 max-w-sm mx-auto">Define who needs what information, how often, and through which channel.</p>
            <div className="flex items-center justify-center gap-2">
              <Button onClick={seedTemplate} className="rounded-full bg-violet-600 hover:bg-violet-700 text-white font-semibold">
                <Sparkles className="w-4 h-4 mr-1.5" /> Start from template
              </Button>
              <Button onClick={() => setPlanForm({ ...BLANK_PLAN })} variant="outline" className="rounded-full border-gray-200">
                <Plus className="w-4 h-4 mr-1.5" /> Add manually
              </Button>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white shadow-sm">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100 text-left">
                  {["Audience / Stakeholder", "Information & purpose", "Frequency", "Channel", "Owner", "Format", ""].map((h, i) => (
                    <th key={i} className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-gray-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {plan.map((row) => (
                  <tr key={row.entry_id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/60 group">
                    <td className="px-4 py-3 font-semibold text-gray-900 align-top">
                      <div className="flex items-center gap-2"><Users className="w-3.5 h-3.5 text-violet-400 shrink-0" />{row.audience}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 align-top max-w-xs">{row.purpose}</td>
                    <td className="px-4 py-3 align-top">
                      <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${FREQ_BADGE[row.frequency] || "bg-gray-100 text-gray-600"}`}>{row.frequency}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-700 align-top">{row.channel}</td>
                    <td className="px-4 py-3 text-gray-700 align-top">{row.owner || "—"}</td>
                    <td className="px-4 py-3 text-gray-500 align-top">{row.format || "—"}</td>
                    <td className="px-4 py-3 align-top whitespace-nowrap">
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => setPlanForm({ ...row })} className="p-1.5 rounded-lg text-gray-400 hover:text-violet-600 hover:bg-violet-50" title="Edit">
                          <FileText className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => removePlan(row)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50" title="Remove">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* ── STAKEHOLDER ANALYSIS TAB ── */}
      {tab === "stakeholders" && (
        stakeholders.length === 0 ? (
          <div className="text-center py-20 border-2 border-dashed border-gray-200 rounded-2xl">
            <div className="w-14 h-14 rounded-2xl bg-violet-100 grid place-items-center mx-auto mb-4">
              <Target className="w-7 h-7 text-violet-500" />
            </div>
            <h3 className="font-semibold text-gray-700">No stakeholders mapped yet</h3>
            <p className="text-sm text-gray-400 mt-1 mb-5 max-w-sm mx-auto">Map each stakeholder's influence and interest to plan how closely to engage them.</p>
            <Button onClick={() => setShForm({ ...BLANK_SH })} className="rounded-full bg-violet-600 hover:bg-violet-700 text-white font-semibold">
              <Plus className="w-4 h-4 mr-1.5" /> Add stakeholder
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Power / Interest grid */}
            <div>
              <div className="flex items-center justify-between mb-2 text-[11px] uppercase tracking-wider text-gray-400 font-semibold">
                <span>← Low influence · High influence →</span>
                <span>Power / Interest grid</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {["keep_informed", "manage_closely", "monitor", "keep_satisfied"].map((qk) => {
                  const q = QUADRANTS[qk];
                  const members = stakeholders.filter((s) => quadrantOf(s.influence, s.interest) === qk);
                  return (
                    <div key={qk} className={`rounded-2xl border p-4 min-h-[140px] ${q.color}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <span className={`w-2 h-2 rounded-full ${q.dot}`} />
                        <span className="font-semibold text-gray-800 text-sm">{q.label}</span>
                        <span className="text-[10px] text-gray-400 ml-auto">{q.sub}</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {members.length === 0 ? (
                          <span className="text-xs text-gray-400 italic">No one here</span>
                        ) : members.map((s) => (
                          <button key={s.stakeholder_id} onClick={() => setShForm({ ...s })}
                            className="inline-flex items-center gap-1.5 bg-white border border-gray-200 rounded-full pl-1 pr-2.5 py-1 text-xs font-medium text-gray-700 hover:border-violet-300 hover:text-violet-700 shadow-sm">
                            <span className={`w-4 h-4 rounded-full grid place-items-center text-white text-[8px] font-bold ${q.dot}`}>{(s.name[0] || "?").toUpperCase()}</span>
                            {s.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Register table */}
            <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white shadow-sm">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100 text-left">
                    {["Stakeholder", "Role", "Influence", "Interest", "Engagement", "Strategy", ""].map((h, i) => (
                      <th key={i} className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-gray-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {stakeholders.map((s) => (
                    <tr key={s.stakeholder_id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/60 group">
                      <td className="px-4 py-3 font-semibold text-gray-900">{s.name}</td>
                      <td className="px-4 py-3 text-gray-600">{s.role || "—"}</td>
                      <td className="px-4 py-3"><span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${s.influence === "high" ? "bg-violet-100 text-violet-700" : "bg-gray-100 text-gray-600"}`}>{s.influence}</span></td>
                      <td className="px-4 py-3"><span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${s.interest === "high" ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-600"}`}>{s.interest}</span></td>
                      <td className="px-4 py-3"><span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 capitalize ${ENGAGEMENT_BADGE[s.engagement] || "bg-gray-100 text-gray-600"}`}>{s.engagement}</span></td>
                      <td className="px-4 py-3 text-gray-500 max-w-xs truncate">{s.strategy || "—"}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => setShForm({ ...s })} className="p-1.5 rounded-lg text-gray-400 hover:text-violet-600 hover:bg-violet-50"><FileText className="w-3.5 h-3.5" /></button>
                          <button onClick={() => removeSh(s)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      )}

      {/* ── LOG TAB ── */}
      {tab === "log" && (
        <>
          <div className="flex bg-gray-50 border border-gray-200 rounded-xl overflow-hidden w-fit mb-5">
            <button onClick={() => setFilter(f => ({ ...f, channel: "" }))} className={`px-3 py-2 text-sm ${!filter.channel ? "bg-white text-violet-700 font-medium shadow-sm" : "text-gray-500 hover:text-gray-800"}`}>All</button>
            {CHANNELS.map(c => (
              <button key={c.key} onClick={() => setFilter(f => ({ ...f, channel: f.channel === c.key ? "" : c.key }))}
                className={`px-3 py-2 text-sm ${filter.channel === c.key ? "bg-white text-violet-700 font-medium shadow-sm" : "text-gray-500 hover:text-gray-800"}`}>
                {c.label}
              </button>
            ))}
          </div>

          {comms.length === 0 ? (
            <div className="text-center py-20 text-gray-400 text-sm border-2 border-dashed border-gray-200 rounded-2xl">No communications logged yet.</div>
          ) : (
            <div className="space-y-3">
              {comms.map(c => (
                <div key={c.comm_id} className="bg-white border border-gray-100 rounded-2xl p-4 hover:shadow-md hover:border-violet-100 group transition-all">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className={`p-2 rounded-xl border shrink-0 ${CHANNEL_COLORS[c.channel] || "text-gray-500 bg-gray-100 border-gray-200"}`}>
                        <ChIcon channel={c.channel} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-gray-900 text-sm">{c.subject}</span>
                          {c.direction === "inbound" ? (
                            <span className="flex items-center gap-0.5 text-[10px] text-emerald-700 bg-emerald-100 border border-emerald-200 px-1.5 py-0.5 rounded-full">
                              <ArrowDownLeft className="w-2.5 h-2.5" /> Inbound
                            </span>
                          ) : (
                            <span className="flex items-center gap-0.5 text-[10px] text-gray-600 bg-gray-100 border border-gray-200 px-1.5 py-0.5 rounded-full">
                              <ArrowUpRight className="w-2.5 h-2.5" /> Outbound
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">{c.body}</p>
                        <div className="flex items-center gap-3 mt-2 flex-wrap text-[10px] text-gray-400">
                          {c.project_id && <span>📁 {projectName(c.project_id)}</span>}
                          {c.contact_id && <span>👤 {contactName(c.contact_id)}</span>}
                          <span>{c.sent_at ? new Date(c.sent_at).toLocaleString() : ""}</span>
                        </div>
                      </div>
                    </div>
                    <button onClick={() => remove(c)} className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 shrink-0">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── Plan add/edit modal ── */}
      {planForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-bold text-gray-900 text-lg">{planForm.entry_id ? "Edit plan row" : "Add communication plan row"}</h2>
              <button onClick={() => setPlanForm(null)} className="p-2 rounded-xl hover:bg-gray-100"><X className="w-4 h-4 text-gray-500" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Audience / Stakeholder *</label>
                <input value={planForm.audience} onChange={e => setPlanForm(f => ({ ...f, audience: e.target.value }))}
                  placeholder="e.g. Project Sponsor" className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Information & purpose *</label>
                <textarea value={planForm.purpose} onChange={e => setPlanForm(f => ({ ...f, purpose: e.target.value }))} rows={2}
                  placeholder="What information do they need and why?" className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-violet-300" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Frequency</label>
                  <select value={planForm.frequency} onChange={e => setPlanForm(f => ({ ...f, frequency: e.target.value }))}
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300">
                    {FREQUENCIES.map(x => <option key={x}>{x}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Channel / Method</label>
                  <select value={planForm.channel} onChange={e => setPlanForm(f => ({ ...f, channel: e.target.value }))}
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300">
                    {PLAN_CHANNELS.map(x => <option key={x}>{x}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Owner</label>
                  <input value={planForm.owner} onChange={e => setPlanForm(f => ({ ...f, owner: e.target.value }))}
                    placeholder="Who sends it" className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Format</label>
                  <input value={planForm.format} onChange={e => setPlanForm(f => ({ ...f, format: e.target.value }))}
                    placeholder="e.g. Status report" className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300" />
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <Button onClick={savePlan} disabled={planSaving} className="flex-1 rounded-full bg-violet-600 hover:bg-violet-700 text-white font-semibold">
                {planSaving ? "Saving…" : "Save row"}
              </Button>
              <Button onClick={() => setPlanForm(null)} variant="outline" className="rounded-full border-gray-200">Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Stakeholder add/edit modal ── */}
      {shForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-bold text-gray-900 text-lg">{shForm.stakeholder_id ? "Edit stakeholder" : "Add stakeholder"}</h2>
              <button onClick={() => setShForm(null)} className="p-2 rounded-xl hover:bg-gray-100"><X className="w-4 h-4 text-gray-500" /></button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Name *</label>
                  <input value={shForm.name} onChange={e => setShForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Jane Smith" className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Role</label>
                  <input value={shForm.role} onChange={e => setShForm(f => ({ ...f, role: e.target.value }))}
                    placeholder="e.g. Sponsor" className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Influence (power)</label>
                  <select value={shForm.influence} onChange={e => setShForm(f => ({ ...f, influence: e.target.value }))}
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300">
                    <option value="high">High</option><option value="low">Low</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Interest</label>
                  <select value={shForm.interest} onChange={e => setShForm(f => ({ ...f, interest: e.target.value }))}
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300">
                    <option value="high">High</option><option value="low">Low</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Engagement level</label>
                  <select value={shForm.engagement} onChange={e => setShForm(f => ({ ...f, engagement: e.target.value }))}
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm capitalize focus:outline-none focus:ring-2 focus:ring-violet-300">
                    {ENGAGEMENTS.map(x => <option key={x} value={x} className="capitalize">{x}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Engagement strategy</label>
                <textarea value={shForm.strategy} onChange={e => setShForm(f => ({ ...f, strategy: e.target.value }))} rows={2}
                  placeholder="How will you engage / influence this stakeholder?" className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-violet-300" />
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <Button onClick={saveSh} disabled={shSaving} className="flex-1 rounded-full bg-violet-600 hover:bg-violet-700 text-white font-semibold">
                {shSaving ? "Saving…" : "Save stakeholder"}
              </Button>
              <Button onClick={() => setShForm(null)} variant="outline" className="rounded-full border-gray-200">Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Log modal ── */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-bold text-gray-900 text-lg">Log communication</h2>
              <button onClick={() => setShowModal(false)} className="p-2 rounded-xl hover:bg-gray-100"><X className="w-4 h-4 text-gray-500" /></button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Direction</label>
                  <div className="flex rounded-xl overflow-hidden border border-gray-200">
                    {["outbound", "inbound"].map(d => (
                      <button key={d} onClick={() => setForm(f => ({ ...f, direction: d }))}
                        className={`flex-1 py-2 text-sm capitalize ${form.direction === d ? "bg-violet-600 text-white" : "text-gray-500 hover:text-gray-800"}`}>{d}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Channel</label>
                  <select value={form.channel} onChange={e => setForm(f => ({ ...f, channel: e.target.value }))}
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300">
                    {CHANNELS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Subject *</label>
                <input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Body *</label>
                <textarea value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} rows={4}
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-violet-300" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Project (optional)</label>
                  <select value={form.project_id} onChange={e => setForm(f => ({ ...f, project_id: e.target.value }))}
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300">
                    <option value="">None</option>
                    {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Contact (optional)</label>
                  <select value={form.contact_id} onChange={e => setForm(f => ({ ...f, contact_id: e.target.value }))}
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300">
                    <option value="">None</option>
                    {contacts.map(c => <option key={c.contact_id || c.id} value={c.contact_id || c.id}>{c.name || c.email}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Date & time</label>
                <input type="datetime-local" value={form.sent_at} onChange={e => setForm(f => ({ ...f, sent_at: e.target.value }))}
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300" />
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <Button onClick={save} disabled={saving} className="flex-1 rounded-full bg-violet-600 hover:bg-violet-700 text-white font-semibold">
                {saving ? "Saving…" : "Log it"}
              </Button>
              <Button onClick={() => setShowModal(false)} variant="outline" className="rounded-full border-gray-200">Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
